import { useCallback, useRef, type RefObject } from 'react'
import { APP_SETTINGS } from '../config/appSettings'

/**
 * The exit of a bottom sheet (design/studio/sheets.css owns how it looks). A dialog is closed at once, which starts its exit; what removes the
 * sheet from the screen runs when the exit is over, so the sheet is never taken away mid-motion. Every close of a sheet by its own controls
 * (its close button, Escape, a tap outside, Done) goes through here; a sheet the screen takes away (the thing it was about is gone) leaves as it is.
 */
export const afterExit = (done: () => void): void => { window.setTimeout(done, APP_SETTINGS.motion.quickMs) }

export function leaveSheet(dialog: HTMLDialogElement | null, done: () => void): void {
  if (dialog?.open) {
    if (typeof dialog.close === 'function') dialog.close()
    else dialog.removeAttribute('open')
  }
  afterExit(done)
}

/** `leave` for a sheet component: closes its dialog, then calls `onClosed` once. */
export function useLeaveSheet(dialog: RefObject<HTMLDialogElement | null>, onClosed: () => void): () => void {
  const leaving = useRef(false), latest = useRef(onClosed)
  latest.current = onClosed
  return useCallback(() => {
    if (leaving.current) return
    leaving.current = true
    leaveSheet(dialog.current, () => { leaving.current = false; latest.current() })
  }, [dialog])
}
